import {
  DevelopmentType,
  DiFormat,
  DurationFormat,
  DurationOption,
  LossFunction,
  Model,
  ModelParameter,
  ModelType,
  ProductionStream,
  QiBoundType,
  QiFormat,
  ResidualFunction,
  TimeFormat,
} from 'types/dca-scenarios-api/responses';
import type { ProductionDataSource } from 'types/api-responses';

interface BaseProductionStreamConfigData {
  id: string | null;
  stream: ProductionStream;
  modelType: ModelType;
  model: Model;
  qiBoundType: QiBoundType;
  lossFunction: LossFunction;
  residualFunction: ResidualFunction;
  alpha: number;
  maxMonthsOnline: number;
  href?: string;
}

export interface UnbiasedProductionStreamConfigData extends BaseProductionStreamConfigData {
  modelType: 'unbiased';
  initialGuess: Record<ModelParameter, number>;
  lowerBounds: Record<ModelParameter, number>;
  upperBounds: Record<ModelParameter, number>;
  lockedParameters: Record<ModelParameter, number | null>;
}

export interface BiasedProductionStreamConfigData extends BaseProductionStreamConfigData {
  modelType: 'biased';
  lossFunction: LossFunction | 'mlle';
  initialGuess: Record<ModelParameter | 'eurMbbl', number>;
  lowerBounds: Record<ModelParameter | 'eurMbbl', number>;
  upperBounds: Record<ModelParameter | 'eurMbbl', number>;
  lockedParameters: Record<ModelParameter | 'eurMbbl', number | null>;
  distributions: Record<ModelParameter | 'eurMbbl', string>;
  residualSigma: number;
  probabilityDistance: number;
  constrainEur: boolean;
}

export interface EarlyTimeProductionStreamConfigData extends BaseProductionStreamConfigData {
  modelType: 'early_time';
  lossFunction: LossFunction | 'mlle';
  residualSigma: number;
  probabilityDistance: number;
  eurToleranceFactor: number;
  lateralLengthDegradationFactor: number;
  lateralLengthDegradationFt: number;
  initialGuess: Record<ModelParameter, number>;
  lowerBounds: Record<ModelParameter, number>;
  upperBounds: Record<ModelParameter, number>;
  lockedParameters: Record<ModelParameter, number | null>;
}

export interface RampUpProductionStreamConfigData extends BaseProductionStreamConfigData {
  modelType: 'ramp_up';
  initialGuess: Record<ModelParameter, number>;
  lowerBounds: Record<ModelParameter, number>;
  upperBounds: Record<ModelParameter, number>;
  lockedParameters: Record<ModelParameter, number | null>;
  earlyTimeRampUpOnly: boolean;
}

export type ProductionStreamConfigData =
  | UnbiasedProductionStreamConfigData
  | BiasedProductionStreamConfigData
  | EarlyTimeProductionStreamConfigData
  | RampUpProductionStreamConfigData;

export interface GlobalConfig {
  timeFormat: TimeFormat;
  qiFormat: QiFormat;
  notes: string;
  name: string;
  diFormat: DiFormat;
  durationOption: DurationOption;
  durationFormat: DurationFormat;
  durationPeriod: number;
  createdBy: string;
}

export interface AreaConfigs {
  areaGeometryId: string | null;
  areaGeometryName: string | null;
  formation: string | null;
  developmentType: DevelopmentType;
}

export interface GeneralConfig {
  id: string | null;
  historyCutoffMonths: number;
  earlyTimeCutoffMonths: number;
  detectPeakRate: boolean;
  peakRateSearchWindow: number | null;
  /** Production data sources in order of preference; the first one a well has is used. */
  productionDataSourceHierarchy: ProductionDataSource[];
}

export interface SegmentationConfig {
  id: string | null;
  method: string;
  targetSegment?: string;
  fallbackToPreviousSegment?: boolean;
  parameters?: {
    model?: string;
    penalty?: number;
    segmentMinLength?: number;
    jumpSize?: number;
  };
}

export interface OutlierConfig {
  id: string | null;
  method?: string;
  parameters?: {
    contamination?: number;
    probabilityThreshold?: number;
    numIterations?: number;
  };
}

export type StreamConfigKey =
  'oilStreamConfigs' | 'gasStreamConfigs' | 'waterStreamConfigs';

export const STREAM_CONFIG_KEYS: Record<string, StreamConfigKey> = {
  oil: 'oilStreamConfigs',
  gas: 'gasStreamConfigs',
  water: 'waterStreamConfigs',
};

export interface ScenarioFormData {
  globalConfig: GlobalConfig | null;

  areaConfigs: AreaConfigs | null;

  generalConfig: GeneralConfig | null;
  segmentationConfig: SegmentationConfig | null;
  outlierConfig: OutlierConfig | null;

  oilStreamConfigs: ProductionStreamConfigData[] | null;
  gasStreamConfigs: ProductionStreamConfigData[] | null;
  waterStreamConfigs: ProductionStreamConfigData[] | null;
}

export interface AreaFormationResponse {
  totalCount: number;
  results: string[];
}
